import type { PatientsSearchT } from "@/routes/_app/patients";
import type { DiariesCountT, DiaryViewT, StateKeyT, StatefulDiaryT } from "@patient-watch/types";
import type { Auth } from "@/utils/auth";
import type { SupportedLocale } from "@patient-watch/i18n";
import { keepPreviousData, queryOptions, type QueryClient } from "@tanstack/react-query";
import { validate as validateUUID } from "uuid";
import {
    getAllDiaries,
    getDiariesBySettings,
    getDiariesTabs,
    getDiaryById,
    getOtherTabCounts,
    getPatientDiaries,
    getPatientDiaryStatuses,
    searchDiariesForPatientDialog,
} from "./diaryApi";
import type { DiariesBySettingsResult, DiarySearchDataT } from "./diaryApi";
import { diaryKeys } from "./diaryKeys";

const defaultQueryConfig = {
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),
} as const;

// ---------------------------------------------------------------------------
// Patients-page query options
// ---------------------------------------------------------------------------
//
// The patients page loads diary rows and all tab counts via list_patient_diaries RPC.
// ---------------------------------------------------------------------------

/**
 * Paginated diary rows for the patients table + exact count for the current tab.
 */
export const diariesBySettingsQueryOptions = (settings: PatientsSearchT, auth: Auth) => {
    return queryOptions<DiariesBySettingsResult>({
        queryKey: diaryKeys.bySettings(settings),
        queryFn: () => getDiariesBySettings(settings, auth),
        ...defaultQueryConfig,
        enabled: !!settings.tab,
        // Keep showing previous data while new sort/page loads to avoid flash
        placeholderData: keepPreviousData,
    });
};

export const patientDiaryStatusesQueryOptions = (settings: PatientsSearchT, auth: Auth) => {
    const { diary_statuses: _diaryStatuses, pagination: _pagination, sorting: _sorting, ...statusFilters } = settings;
    return queryOptions<StateKeyT[]>({
        queryKey: diaryKeys.statuses(statusFilters),
        queryFn: () => getPatientDiaryStatuses(settings, auth),
        ...defaultQueryConfig,
        enabled: !!settings.tab && !!auth.id,
        placeholderData: keepPreviousData,
    });
};

/**
 * Counts for the two non-current tabs (the current tab count comes from
 * diariesBySettingsQueryOptions for free).
 */
export const otherTabCountsQueryOptions = (settings: PatientsSearchT, auth: Auth) => {
    return queryOptions<DiariesCountT>({
        queryKey: diaryKeys.count(settings),
        queryFn: () => getOtherTabCounts(settings, auth),
        ...defaultQueryConfig,
        enabled: !!settings.tab,
        placeholderData: keepPreviousData,
    });
};

/**
 * Server-side patient search for the search dialog.
 * Only runs when the dialog is open and the query is at least 2 characters.
 */
export const patientDiarySearchQueryOptions = (query: string, enabled: boolean) => {
    const trimmed = query.trim();

    return queryOptions<DiarySearchDataT[]>({
        queryKey: [...diaryKeys.all, "search", trimmed],
        queryFn: () => searchDiariesForPatientDialog(trimmed),
        enabled: enabled && trimmed.length >= 2,
        staleTime: 30_000,
        gcTime: 60_000,
    });
};

// ---------------------------------------------------------------------------
// Diary detail query options (used outside the patients page)
// ---------------------------------------------------------------------------

export const allDiariesQueryOptions = () => {
    return queryOptions<DiaryViewT[]>({
        queryKey: diaryKeys.all,
        queryFn: () => getAllDiaries(),
        ...defaultQueryConfig,
    });
};

export const diaryByIdQueryOptions = (diaryId: string, locale?: SupportedLocale) => {
    if (!validateUUID(diaryId)) {
        return queryOptions<StatefulDiaryT | null>({
            queryKey: diaryKeys.detail(diaryId),
            queryFn: () => Promise.resolve(null),
            ...defaultQueryConfig,
        });
    }

    return queryOptions<StatefulDiaryT | null>({
        queryKey: [...diaryKeys.detail(diaryId), locale ?? "en-GB"] as const,
        queryFn: () => getDiaryById(diaryId, locale),
        ...defaultQueryConfig,
        // This is an expensive edge function (RPC → state machine). Explicit
        // mutation invalidation is the source of truth for freshness, so:
        //   - High staleTime: navigating back after a fresh fetch does not re-fire
        //   - refetchOnWindowFocus off: Playwright / tab-switches don't pile up requests
        //   - retry: 1: a failing DB under load must not retry 3× and make it worse
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });
};

export const diariesByPatientQueryOptions = (patientId: string) => {
    if (!validateUUID(patientId)) {
        return queryOptions<StatefulDiaryT[]>({
            queryKey: diaryKeys.byPatient(patientId),
            queryFn: () => Promise.resolve([]),
            ...defaultQueryConfig,
        });
    }

    return queryOptions<StatefulDiaryT[]>({
        queryKey: diaryKeys.byPatient(patientId),
        queryFn: () => getPatientDiaries(patientId),
        ...defaultQueryConfig,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });
};

/** Bypass the patient-diaries cache (used after signup when an empty list may be stale). */
export async function fetchPatientDiariesFresh(queryClient: QueryClient, patientId: string) {
    return queryClient.fetchQuery({
        ...diariesByPatientQueryOptions(patientId),
        staleTime: 0,
    });
}

/** Poll until quickstart diaries appear or attempts are exhausted. */
export async function pollPatientDiaries(
    queryClient: QueryClient,
    patientId: string,
    options?: { maxAttempts?: number; intervalMs?: number }
) {
    const maxAttempts = options?.maxAttempts ?? 15;
    const intervalMs = options?.intervalMs ?? 1000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const diaries = await fetchPatientDiariesFresh(queryClient, patientId);
        if (diaries.length > 0) {
            return diaries;
        }
        if (attempt < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
    }

    return fetchPatientDiariesFresh(queryClient, patientId);
}

export const diariesTabsQueryOptions = (filters: Record<string, unknown> = {}) => {
    return queryOptions<{ tab: string | null }[]>({
        queryKey: diaryKeys.tabs(filters),
        queryFn: () => getDiariesTabs(filters),
        ...defaultQueryConfig,
    });
};
